import React from 'react'

import { useQueryClient } from '@tanstack/react-query'
import {
  useOnboardingData,
  useTeamPreferencesStatus,
  useUpdateStepCompletion,
} from 'hooks/useOnboardingQueries'
import { queryKeys } from 'lib/queryClient'
import { AlertTriangle, Check, Copy, Phone, Play } from 'lucide-react'
import type { OnboardingStep as BaseOnboardingStep } from 'services/onboardingApi'

import { Alert } from 'app/components/design-system-components/Alert'
import Box from 'app/components/design-system-components/Box'
import { Button } from 'app/components/design-system-components/Button'
import Card from 'app/components/design-system-components/Card'
import Flex from 'app/components/design-system-components/Flex'
import { ProgressBar as BaseProgressBar } from 'app/components/design-system-components/ProgressBar'
import Select from 'app/components/design-system-components/Select'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import {
  createMailtoUrl,
  ONBOARDING_EMAIL_TEMPLATES,
} from 'app/pages/teams/settings/onboarding/constants/emailTemplates'
import { PROPERTY_UPLOADERS } from 'app/pages/teams/settings/onboarding/constants/propertyUploaders'
import { ONBOARDING_SECTIONS } from 'app/pages/teams/settings/onboarding/constants/sections'
import { ONBOARDING_STEP_IDS } from 'app/pages/teams/settings/onboarding/constants/stepIds'
import { EMAIL_TEMPLATES } from 'app/pages/teams/settings/onboarding/constants/templates'
import * as Styled from 'app/pages/teams/settings/onboarding/styled/OnboardingStyles'
import {
  getBundleDescription,
  getPreferenceItem,
  TeamPreferences,
  TeamPreferencesData,
} from 'app/pages/teams/settings/onboarding/types/preferences'
import { toggleEnquiryResponder } from 'app/services/http/messages'
import * as snugNotifier from 'app/services/snugNotifier'
import { selectAccountType } from 'app/utils/accounts/helpers'
import * as widgetHelpers from 'app/utils/widgets/helpers'

const PIPEDRIVE_LINK =
  'https://snugcom.pipedrive.com/scheduler/49oqjuq/lets-get-you-set-up'
const ACTIVE = 'active'

// Wrapper component to handle ProgressBar's potential empty string return
const ProgressBar: React.FC<any> = (props) => {
  const result = BaseProgressBar(props)
  return result === '' ? null : result
}

// Step interfaces - extend the base interface with UI-specific properties
export interface OnboardingStep
  extends Omit<
    BaseOnboardingStep,
    | 'completed'
    | 'actionText'
    | 'buttonOnClickHandler'
    | 'showVideo'
    | 'section'
    | 'secondaryButtonOnClickHandler'
  > {
  completed: boolean // Make completed required for UI
  actionText?: string // Make actionText optional for UI
  buttonOnClickHandler?: (() => void) | undefined // Make buttonOnClickHandler optional for UI
  showVideo?: boolean // Make showVideo optional for UI
  section?: string // Make section optional for UI
  additionalContent?: React.ReactNode
  buttonDisabled?: boolean
  // Secondary button properties - override to allow undefined
  secondaryButtonOnClickHandler?: (() => void) | undefined
  secondaryButtonDisabled?: boolean
  // Additional properties found in the codebase
  preferenceDescription?: string | undefined
  preferenceStatus?: string | undefined
  preferenceMessage?: string | undefined
}

interface OnboardingPageWithAPIProps {
  // Redux props
  currentTeam?: any
  currentUser?: any
  currentAgency?: any
  // Optional overrides
  userName?: string
  organizationName?: string
  onActionClick?: (stepId: string, actionType: string) => void
}

// Utility function to get Vimeo thumbnail URL from video URL
const getVimeoThumbnailUrl = (videoUrl: string): string | null => {
  // Handle both public and unlisted/private Vimeo URLs
  // Public: https://vimeo.com/123456789
  // Unlisted/Private: https://vimeo.com/744850460/7cf4b29f00
  const match = videoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-f0-9]+))?/)
  if (match && match[1]) {
    const videoId = match[1]
    const privacyHash = match[2]

    if (privacyHash) {
      // For unlisted/private videos: https://vumbnail.com/videoId:privacyHash.jpg
      return `https://vumbnail.com/${videoId}:${privacyHash}.jpg`
    } else {
      // For public videos: https://vumbnail.com/videoId.jpg
      return `https://vumbnail.com/${videoId}.jpg`
    }
  }
  return null
}

export const SnugOnboardingPageWithAPI: React.FC<OnboardingPageWithAPIProps> =
  ({
    currentTeam,
    currentUser,
    currentAgency,
    userName,
    organizationName,
    onActionClick = () => {},
  }) => {
    const teamGUID = currentTeam?.GUID || currentTeam?.guid
    const agencyGUID = currentTeam?.GUID || currentTeam?.guid // Same as teamGUID for this API

    // Prepare user data for API calls
    const userData = {
      currentUser,
      currentTeam,
      currentAgency,
    }

    // API hooks
    const {
      data: onboardingData,
      isLoading,
      error,
    } = useOnboardingData(teamGUID, userData)

    const {
      data: preferencesData,
      isLoading: isLoadingPreferences,
      error: preferencesError,
    } = useTeamPreferencesStatus(agencyGUID) as {
      data: TeamPreferencesData | undefined
      isLoading: boolean
      error: any
    }

    const updateStepMutation = useUpdateStepCompletion(teamGUID)
    const queryClient = useQueryClient()

    // State for autoresponder toggle
    const [isTogglingAutoresponder, setIsTogglingAutoresponder] =
      React.useState(false)

    // State for property uploader selection
    const [selectedPropertyUploader, setSelectedPropertyUploader] =
      React.useState('')

    // Use API data when available, fallback to Redux/props
    const displayUserName =
      onboardingData?.user?.userName ||
      currentUser?.firstName ||
      currentUser?.name ||
      userName ||
      'User'

    const displayOrganizationName =
      onboardingData?.user?.organizationName ||
      currentTeam?.name ||
      organizationName ||
      'Your Organization'

    const displayPlan = currentTeam?.accountType
      ? selectAccountType(currentTeam.accountType)
      : 'N/A'

    // Handle checkbox click
    const handleStepToggle = (stepId: string, currentCompleted: boolean) => {
      if (!teamGUID) {
        snugNotifier.error('No team ID available for step update')
        return
      }

      // Check if the mutation function exists and has a mutate method
      if (
        updateStepMutation &&
        typeof updateStepMutation.mutate === 'function'
      ) {
        ;(updateStepMutation.mutate as any)({
          stepId,
          completed: !currentCompleted,
        })
      } else {
        snugNotifier.error('Error updating the step')
      }
    }

    // Handle action button click
    const handleActionClick = (stepId: string, actionType: string) => {
      onActionClick(stepId, actionType)
    }

    // Handle autoresponder toggle
    const handleAutoresponderToggle = async (currentStatus: string) => {
      if (!teamGUID) {
        snugNotifier.error('No team ID available for autoresponder toggle')
        return
      }

      try {
        setIsTogglingAutoresponder(true)
        const shouldEnable = currentStatus !== ACTIVE

        await toggleEnquiryResponder(teamGUID, shouldEnable)

        // Invalidate and refetch preferences data to get updated status
        await queryClient.invalidateQueries({
          queryKey: queryKeys.onboarding.preferences(agencyGUID),
        })
      } catch (error) {
        snugNotifier.error(
          error instanceof Error
            ? error.message
            : 'Failed to toggle autoresponder',
        )
        // You could add a toast notification here for user feedback
      } finally {
        setIsTogglingAutoresponder(false)
      }
    }

    // Handle property uploader email dialog
    const handlePropertyUploaderEmail = (uploaderId: string) => {
      const template =
        EMAIL_TEMPLATES[uploaderId as keyof typeof EMAIL_TEMPLATES]

      if (!template) {
        snugNotifier.error(
          `No email template found for uploader: ${uploaderId}`,
        )
        return
      }

      // Create mailto URL with all the email details
      const subject = encodeURIComponent(template.subject)
      const body = encodeURIComponent(template.body)
      const cc = encodeURIComponent(template.cc)

      const mailtoUrl = `mailto:${template.to}?cc=${cc}&subject=${subject}&body=${body}`

      // Open the default email client
      window.location.href = mailtoUrl
    }

    // Helper function to check if Integration bundle is enabled
    const hasIntegrationBundle = (
      bundlesString: string | undefined,
    ): boolean => {
      if (!bundlesString) return false
      return bundlesString.includes('Integration: On')
    }

    const StepCard: React.FC<{ step: OnboardingStep }> = ({ step }) => (
      <Styled.StepCardContainer>
        <Styled.StepCardContent>
          {/* Checkbox */}
          <Styled.CheckboxContainer
            completed={step.completed}
            pending={updateStepMutation.isPending}
            onClick={() => handleStepToggle(step.id, step.completed)}
            title="Click to toggle completion status"
          >
            {step.completed && <Check size={16} />}
            {updateStepMutation.isPending && <Styled.LoadingSpinner />}
          </Styled.CheckboxContainer>

          <Styled.ContentContainer>
            {step.id ===
              ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM &&
              !hasIntegrationBundle(
                getBundleDescription(preferencesData?.preferences),
              ) && (
                <Styled.AlertContainer>
                  <Styled.AlertContent>
                    <AlertTriangle size={20} color="#f59e0b" />
                    <Styled.AlertTextContainer>
                      <Styled.AlertTitle>
                        Integration Bundle Required
                      </Styled.AlertTitle>
                      <Styled.AlertDescription>
                        Enable the Integration bundle to connect your Property
                        Management System
                      </Styled.AlertDescription>
                    </Styled.AlertTextContainer>
                  </Styled.AlertContent>
                </Styled.AlertContainer>
              )}

            {/* Title and Description Section with Video Button */}
            <Styled.TitleDescriptionContainer>
              <Styled.TextContainer>
                <Styled.StepTitle>{step.title}</Styled.StepTitle>
                <Styled.StepDescription>
                  {step.description}
                </Styled.StepDescription>
                {/* Additional Content Section - Now on its own line */}
                {step.additionalContent && (
                  <Styled.AdditionalContentContainer>
                    {step.additionalContent}
                  </Styled.AdditionalContentContainer>
                )}
                {/* Status Section */}
                {((step.id === ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS &&
                  step.preferenceDescription) ||
                  (step.id ===
                    ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER &&
                    step.preferenceStatus === ACTIVE) ||
                  (step.id === ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER &&
                    step.preferenceStatus === ACTIVE) ||
                  (step.id === ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER &&
                    step.preferenceStatus === ACTIVE) ||
                  (step.id ===
                    ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM &&
                    step.preferenceStatus === ACTIVE) ||
                  step.completed) && (
                  <Styled.StatusContainer>
                    {/* Special handling for Invite Team Members with preference data */}
                    {step.id === ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS &&
                    step.preferenceDescription ? (
                      (() => {
                        const memberCount =
                          parseInt(step.preferenceDescription) || 0
                        const isMoreThanThree = memberCount > 3

                        return (
                          <>
                            {isMoreThanThree && (
                              <Check size={16} color="#10b981" />
                            )}
                            <Styled.StatusText success={isMoreThanThree}>
                              {isMoreThanThree ? 'Completed' : 'Progress'} -{' '}
                              {step.preferenceDescription} team members added
                            </Styled.StatusText>
                          </>
                        )
                      })()
                    ) : step.id ===
                        ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER &&
                      step.preferenceStatus === ACTIVE ? (
                      /* Special handling for AutoResponder when active */
                      <>
                        <Check size={16} color="#10b981" />
                        <Styled.StatusText success={true}>
                          Enabled
                        </Styled.StatusText>
                      </>
                    ) : step.id ===
                        ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER &&
                      step.preferenceStatus === ACTIVE ? (
                      /* Special handling for Property Uploader when active */
                      <>
                        <Check size={16} color="#10b981" />
                        <Styled.StatusText success={true}>
                          Enabled
                        </Styled.StatusText>
                      </>
                    ) : step.id ===
                        ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER &&
                      step.preferenceStatus === ACTIVE ? (
                      /* Special handling for Utility Provider when active */
                      <>
                        <Check size={16} color="#10b981" />
                        <Styled.StatusText success={true}>
                          Enabled
                        </Styled.StatusText>
                      </>
                    ) : step.id ===
                        ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM &&
                      step.preferenceStatus === ACTIVE ? (
                      /* Special handling for Property Management System when active */
                      <>
                        <Check size={16} color="#10b981" />
                        <Styled.StatusText success={true}>
                          Enabled
                        </Styled.StatusText>
                      </>
                    ) : step.completed ? (
                      /* Generic completion status for other steps */
                      <>
                        <Check size={16} color="#10b981" />
                        <Styled.StatusText success={true}>
                          Complete
                        </Styled.StatusText>
                      </>
                    ) : null}
                  </Styled.StatusContainer>
                )}
                {/* Apply Link URL display */}
                {step.id === ONBOARDING_STEP_IDS.APPLY_LINK && step.applyUrl && (
                  <Styled.UrlDisplayContainer>
                    <Styled.UrlDisplayContent>
                      <Styled.UrlText>
                        {step.preferenceDescription}
                      </Styled.UrlText>
                      <Button
                        variant="ghost"
                        sizeVariant="small"
                        onClick={() => {
                          window?.navigator?.clipboard?.writeText(
                            step.preferenceDescription || '',
                          )
                        }}
                        style={{
                          padding: `${theme.space[1]}px`,
                          minWidth: 'auto',
                          height: 'auto',
                        }}
                        title="Copy URL"
                      >
                        <Copy size={16} />
                      </Button>
                    </Styled.UrlDisplayContent>
                  </Styled.UrlDisplayContainer>
                )}
                {/* AutoResponder status display with copy functionality */}
                {step.id === ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER &&
                  step.preferenceDescription && (
                    <Styled.UrlDisplayContainer>
                      <Styled.UrlDisplayContent>
                        <Styled.UrlText>
                          {step.preferenceDescription}
                        </Styled.UrlText>
                        <Button
                          variant="ghost"
                          sizeVariant="small"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              step.preferenceDescription || '',
                            )
                          }}
                          style={{
                            padding: `${theme.space[1]}px`,
                            minWidth: 'auto',
                            height: 'auto',
                          }}
                          title="Copy AutoResponder Status"
                        >
                          <Copy size={16} />
                        </Button>
                      </Styled.UrlDisplayContent>
                    </Styled.UrlDisplayContainer>
                  )}
                {/* Property Uploader status display */}
                {step.id === ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER &&
                  step.preferenceDescription && (
                    <Styled.UrlDisplayContainer>
                      <Styled.UrlDisplayContent>
                        <Styled.UrlText>
                          {step.preferenceDescription}
                        </Styled.UrlText>
                        <Button
                          variant="ghost"
                          sizeVariant="small"
                          onClick={() => {
                            window?.navigator?.clipboard.writeText(
                              step.preferenceDescription || '',
                            )
                          }}
                          style={{
                            padding: `${theme.space[1]}px`,
                            minWidth: 'auto',
                            height: 'auto',
                          }}
                          title="Copy Property Uploader Info"
                        >
                          <Copy size={16} />
                        </Button>
                      </Styled.UrlDisplayContent>
                    </Styled.UrlDisplayContainer>
                  )}
                {/* Utility Provider status display */}
                {step.id === ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER &&
                  step.preferenceDescription && (
                    <Styled.StatusDisplayContainer>
                      <Styled.StatusDisplayContent>
                        <Styled.StatusDisplayValue>
                          {step.preferenceDescription}
                        </Styled.StatusDisplayValue>
                      </Styled.StatusDisplayContent>
                    </Styled.StatusDisplayContainer>
                  )}
                {/* Property Management System status display */}
                {step.id ===
                  ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM &&
                  step.preferenceDescription && (
                    <Styled.UrlDisplayContainer>
                      <Styled.UrlDisplayContent>
                        <Styled.UrlText>
                          {step.preferenceDescription}
                        </Styled.UrlText>
                      </Styled.UrlDisplayContent>
                    </Styled.UrlDisplayContainer>
                  )}{' '}
                {/* Action buttons */}
                {step.actionText &&
                  !(
                    (step.id ===
                      ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM &&
                      step.preferenceStatus === ACTIVE) ||
                    (step.id === ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER &&
                      step.preferenceDescription &&
                      step.preferenceStatus === ACTIVE)
                  ) && (
                    <Styled.ActionButtonContainer>
                      <Button
                        variant="outline"
                        sizeVariant="small"
                        onClick={() => {
                          if (
                            step.id ===
                            ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER
                          ) {
                            handleAutoresponderToggle(
                              step.preferenceStatus || '',
                            )
                          } else if (
                            step.id ===
                              ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER &&
                            step.preferenceStatus !== ACTIVE
                          ) {
                            window.location.href =
                              'mailto:help@snug.com?subject=Connect Utility Provider&body=Please connect the utility provider.'
                          } else if (step.buttonOnClickHandler) {
                            step.buttonOnClickHandler()
                          } else {
                            handleActionClick(step.id, 'primary')
                          }
                        }}
                        disabled={
                          step.buttonDisabled ||
                          (isTogglingAutoresponder &&
                            step.id ===
                              ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER)
                        }
                        style={{
                          marginRight: step.secondaryActionText
                            ? `${theme.space[2]}px`
                            : 0,
                        }}
                      >
                        {step.id ===
                        ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER
                          ? isTogglingAutoresponder
                            ? 'Updating...'
                            : step.preferenceStatus === ACTIVE
                            ? 'Turn off Autoresponder'
                            : 'Turn on Autoresponder'
                          : step.actionText}
                      </Button>
                      {/* Secondary button */}
                      {step.secondaryActionText && (
                        <Button
                          variant="outline"
                          sizeVariant="small"
                          onClick={() => {
                            if (step.secondaryButtonOnClickHandler) {
                              step.secondaryButtonOnClickHandler()
                            } else {
                              handleActionClick(step.id, 'secondary')
                            }
                          }}
                          disabled={step.secondaryButtonDisabled}
                        >
                          {step.secondaryActionText}
                        </Button>
                      )}
                    </Styled.ActionButtonContainer>
                  )}
              </Styled.TextContainer>

              {/* Video button positioned next to title/description */}
              {step.showVideo && (
                <Styled.VideoButton
                  {...(step.videoUrl && getVimeoThumbnailUrl(step.videoUrl)
                    ? { $thumbnailUrl: getVimeoThumbnailUrl(step.videoUrl)! }
                    : {})}
                  onClick={() => {
                    if (step.videoUrl) {
                      window.open(step.videoUrl, '_blank')
                    } else {
                      handleActionClick(step.id, 'video')
                    }
                  }}
                  title="Watch Tutorial"
                >
                  {!(step.videoUrl && getVimeoThumbnailUrl(step.videoUrl)) && (
                    <Play size={16} color="#6b7280" />
                  )}
                </Styled.VideoButton>
              )}
            </Styled.TitleDescriptionContainer>
          </Styled.ContentContainer>
        </Styled.StepCardContent>
      </Styled.StepCardContainer>
    )

    // Loading state
    if (isLoading || isLoadingPreferences) {
      return (
        <Styled.LoadingContainer>
          <Styled.LoadingSpinnerLarge />
          <p>
            Loading onboarding{isLoadingPreferences ? ' and preferences' : ''}{' '}
            data...
          </p>
        </Styled.LoadingContainer>
      )
    }

    // Error state
    if (error) {
      return (
        <Styled.ErrorContainer>
          <Alert severity="error">
            Failed to load onboarding data. Please try refreshing the page.
          </Alert>
          {preferencesError && (
            <Alert
              severity="warning"
              style={{ marginTop: `${theme.space[5]}px` }}
            >
              Warning: Some preference information may not be available.
            </Alert>
          )}
        </Styled.ErrorContainer>
      )
    }

    const steps = onboardingData?.steps || []
    const completedStepsCount = onboardingData?.completedStepsCount || 0
    const progressPercentage = onboardingData?.progressPercentage || 0

    // Enhance steps with preferences data
    const enhancedSteps: OnboardingStep[] = steps.map(
      (step: BaseOnboardingStep): OnboardingStep => {
        // Map step IDs to preference names for enhanced information
        const preferenceMapping: { [key: string]: keyof TeamPreferences } = {
          [ONBOARDING_STEP_IDS.APPLY_LINK]: 'Apply link',
          [ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER]: 'Property uploader',
          [ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER]: 'Autoresponder',
          [ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER]:
            'Utility connection provider',
          'website-notifications': 'Website notifications',
          'email-notifications': 'Email notifications',
          [ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS]:
            'Number of invited team members',
          [ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM]:
            'Property Management System',
          bundles: 'Bundles',
        }

        const preferenceName = preferenceMapping[step.id]
        const preferenceInfo = preferenceName
          ? getPreferenceItem(preferencesData?.preferences, preferenceName)
          : null

        // Add additional content for property uploader step
        if (step.id === ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER) {
          const hasSelection = selectedPropertyUploader !== ''

          // Convert property uploaders to use numeric IDs for Select component compatibility
          const propertyUploadersWithNumericIds = PROPERTY_UPLOADERS.map(
            (uploader, index) => ({
              id: index, // Use numeric index for Select component
              name: uploader.name,
              originalId: uploader.id, // Keep original string ID for reference
            }),
          )

          // Find current selection index
          const currentSelectionIndex = PROPERTY_UPLOADERS.findIndex(
            (uploader) => uploader.id === selectedPropertyUploader,
          )

          return {
            ...step,
            completed: step.completed ?? false, // Ensure completed is always defined
            additionalContent: (
              <Box style={{ marginBottom: `${theme.space[4]}px` }}>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate700,
                    marginBottom: `${theme.space[3]}px`,
                    fontWeight: 500,
                  }}
                >
                  Select your property management system:
                </p>
                <Styled.PropertyUploaderSelectContainer>
                  <Select
                    options={propertyUploadersWithNumericIds}
                    value={
                      currentSelectionIndex >= 0 ? currentSelectionIndex : ''
                    }
                    onSelectChange={(selectData: {
                      value: number
                      selectedIndex: number
                      error: string
                    }) => {
                      // Get the original string ID from our mapping
                      const selectedUploader =
                        PROPERTY_UPLOADERS[selectData.value]
                      if (selectedUploader) {
                        setSelectedPropertyUploader(selectedUploader.id)
                      }
                    }}
                    label="Choose your property uploader..."
                    showSelectLabel={true}
                    id="property-uploader-select"
                    selectName="propertyUploader"
                    validate={() => true}
                    labelText=""
                  />
                </Styled.PropertyUploaderSelectContainer>
              </Box>
            ),
            actionText: 'Request Integration',
            buttonOnClickHandler: hasSelection
              ? () => handlePropertyUploaderEmail(selectedPropertyUploader)
              : undefined,
            // Add a custom disabled state for the button
            buttonDisabled: !hasSelection,
            ...(preferenceInfo
              ? {
                  preferenceStatus: preferenceInfo.status,
                  preferenceMessage: preferenceInfo.message,
                  preferenceDescription: preferenceInfo.description,
                }
              : {}),
          }
        }

        // Add additional content and conditional logic for Property Management System step
        if (
          step.id === ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM
        ) {
          const bundlesData = getBundleDescription(preferencesData?.preferences)
          const hasIntegration = hasIntegrationBundle(bundlesData)

          return {
            ...step,
            completed: step.completed ?? false, // Ensure completed is always defined
            // No additionalContent needed since alert is now at the top of StepCard
            // Conditional button text and functionality based on integration bundle
            actionText: hasIntegration
              ? 'Book a setup call'
              : 'Enable Integration Bundle',
            buttonOnClickHandler: hasIntegration
              ? () => {
                  // Book setup call for accounts with Integration bundle
                  window.open(PIPEDRIVE_LINK, '_blank')
                }
              : () => {
                  // Navigate to bundles/upgrade page for accounts without Integration bundle
                  widgetHelpers.openZendeskWidget()
                },
            ...(preferenceInfo
              ? {
                  preferenceStatus: preferenceInfo.status,
                  preferenceMessage: preferenceInfo.message,
                  preferenceDescription: preferenceInfo.description,
                }
              : {}),
          }
        }

        // If we have preference info for this step, enhance it
        if (preferenceInfo) {
          return {
            ...step,
            completed: step.completed ?? false, // Ensure completed is always defined
            preferenceStatus: preferenceInfo.status, // Changed from currentStatus to status
            preferenceMessage: preferenceInfo.message,
            preferenceDescription: preferenceInfo.description,
          }
        }

        return {
          ...step,
          completed: step.completed ?? false, // Ensure completed is always defined
        }
      },
    )

    // Group steps by section
    const quickStartSteps = enhancedSteps.filter(
      (step) => step.section === ONBOARDING_SECTIONS.QUICK_START,
    )
    const setupSteps = enhancedSteps.filter(
      (step) => step.section === ONBOARDING_SECTIONS.SETUP,
    )
    const optimizeSteps = enhancedSteps.filter(
      (step) => step.section === ONBOARDING_SECTIONS.OPTIMIZE,
    )
    const trainingSteps = enhancedSteps.filter(
      (step) => step.section === ONBOARDING_SECTIONS.TRAINING,
    )
    const legacySteps = enhancedSteps.filter(
      (step) => step.section === ONBOARDING_SECTIONS.LEGACY,
    )

    return (
      <Styled.OnboardingContainer>
        {/* Header Section */}
        <Styled.HeaderContainer>
          <Styled.WelcomeTitle>
            👋 Welcome to Snug, {displayUserName}!
          </Styled.WelcomeTitle>
          <Styled.WelcomeDescription>
            Let's get your property management setup working perfectly. Complete
            these steps to optimise your workflow.
          </Styled.WelcomeDescription>
        </Styled.HeaderContainer>

        {/* Organization & Plan Section */}
        <Card
          headerTitle="Your Organisation & Plan"
          headerImage={null}
          forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
          internalComponent={
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[1]}px`,
                  }}
                >
                  Organisation
                </p>
                <p style={{ fontSize: `${theme.space[5]}px`, fontWeight: 500 }}>
                  {displayOrganizationName}
                </p>
              </Box>
              <Box style={{ textAlign: 'right' }}>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[1]}px`,
                  }}
                >
                  Current Plan
                </p>
                <p style={{ fontSize: `${theme.space[5]}px`, fontWeight: 500 }}>
                  {displayPlan}
                </p>
              </Box>
            </Flex>
          }
        />

        {/* Setup Progress Section */}
        <Card
          headerTitle="Setup Progress"
          headerImage={null}
          forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
          internalComponent={
            <Box>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: `${theme.space[4]}px` }}
              >
                <span
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    fontWeight: 500,
                    color: theme.colors.slate500,
                  }}
                >
                  Setup Progress
                </span>
                <span
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    fontWeight: 500,
                  }}
                >
                  {progressPercentage}% Complete
                </span>
              </Flex>
              <ProgressBar
                totalStepsCount={steps.length}
                lastCheckedStep={completedStepsCount}
                mb={2}
              />
              <p
                style={{
                  fontSize: `${theme.space[4]}px`,
                  color: theme.colors.slate400,
                  marginTop: `${theme.space[2]}px`,
                }}
              >
                {completedStepsCount} of {steps.length} steps completed
              </p>
            </Box>
          }
        />

        {/* Quick Start Section */}
        {quickStartSteps.length > 0 && (
          <Card
            headerTitle="Quick Start"
            headerImage={null}
            forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
            internalComponent={
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[5]}px`,
                  }}
                >
                  Get your property management setup working quickly with these
                  essential steps.
                </p>
                {quickStartSteps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            }
          />
        )}

        {/* Set Up Section */}
        {setupSteps.length > 0 && (
          <Card
            headerTitle="Set Up"
            headerImage={null}
            forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
            internalComponent={
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[5]}px`,
                  }}
                >
                  Complete your integrations and automation features.
                </p>
                {setupSteps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            }
          />
        )}

        {/* Ready for Setup Call Section */}
        <Card
          headerTitle="Ready for Your Setup Call?"
          headerImage={null}
          forcedStyles={{
            marginBottom: `${theme.space[7]}px`,
            borderColor: theme.colors.blue500,
            backgroundColor: theme.colors.blue100,
          }}
          internalComponent={
            <Box>
              <p
                style={{
                  fontSize: `${theme.fontSizes.pRegular14}`,
                  color: theme.colors.blue800,
                  marginBottom: `${theme.space[5]}px`,
                }}
              >
                Our team can help you configure your Auto Responder and connect
                your Property Management System.
              </p>
              <Button
                variant="solidSuccessStrong"
                onClick={() => {
                  window.location.href = createMailtoUrl(
                    ONBOARDING_EMAIL_TEMPLATES.READY_FOR_SETUP_CALL,
                  )
                }}
              >
                <Flex
                  alignItems="center"
                  style={{ gap: `${theme.space[2]}px` }}
                >
                  <Phone size={16} />
                  Book a Setup Call
                </Flex>
              </Button>
            </Box>
          }
        />

        {/* Optimize Section */}
        {optimizeSteps.length > 0 && (
          <Card
            headerTitle="Customise Settings"
            headerImage={null}
            forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
            internalComponent={
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[5]}px`,
                  }}
                >
                  Customize your settings for the best experience.
                </p>
                {optimizeSteps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            }
          />
        )}

        {/* Training and Go Live Section */}
        {trainingSteps.length > 0 && (
          <Card
            headerTitle="Training and Go Live"
            headerImage={null}
            forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
            internalComponent={
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[5]}px`,
                  }}
                >
                  Get comprehensive training and go live with your setup.
                </p>
                {trainingSteps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            }
          />
        )}

        {/* Decommission Legacy Systems Section */}
        {legacySteps.length > 0 && (
          <Card
            headerTitle="Decommission Legacy Systems"
            headerImage={null}
            forcedStyles={{ marginBottom: `${theme.space[7]}px` }}
            internalComponent={
              <Box>
                <p
                  style={{
                    fontSize: `${theme.fontSizes.pRegular14}`,
                    color: theme.colors.slate500,
                    marginBottom: `${theme.space[5]}px`,
                  }}
                >
                  Turn off your current systems once you're fully set up.
                </p>
                {legacySteps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            }
          />
        )}
      </Styled.OnboardingContainer>
    )
  }

export default SnugOnboardingPageWithAPI
